import {css, CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS,} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {consume} from '@lit/context';
import styles from './size-layer.scss';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import baseLayerStyles from '../base-layer/base-layer.scss';
import {Article, Messages} from '../../../@types/wishlist.js';
import './size-layer-item.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {trackSizeLayerOpen} from '../../../scripts/service/cube-tracking-layer.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {getBackButton} from '../base-layer/base-layer.js';

@customElement('ents-size-layer')
export class SizeLayer extends LitElement {
  @property({type: Boolean, attribute: 'logged-in'}) private loggedIn: boolean = false;

  @consume({context: sizeLayerContext, subscribe: true})
  @property({attribute: false})
  private sizeLayerState!: SizeLayerState;

  @consume({context: i18nContext})
  private messages!: Messages;

  @state()
  private showLayer: boolean = false;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(baseLayerStyles)}`];
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('ents:size-layer-item:selected', this.closeLayer);
    document.addEventListener('ents:article:addtocart', this.closeLayer); // addtocart dispatched by BRTN
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('ents:size-layer-item:selected', this.closeLayer);
    document.removeEventListener('ents:article:addtocart', this.closeLayer);
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    const previousSizeLayerState: SizeLayerState | undefined = _changedProperties.get('sizeLayerState');
    if (previousSizeLayerState != null && !previousSizeLayerState.open && this.sizeLayerState.open) {
      trackSizeLayerOpen(this.loggedIn, this.sizeLayerState.trackingContext);
      this.showLayer = true;
    }
  }

  protected closeLayer = () => {
    if (this.showLayer) {
      this.showLayer = false;
    }
  };

  private backButtonAction = () => {
    this.handleLayerClosed();
    if (this.sizeLayerState.backButtonCallback) {
      this.sizeLayerState.backButtonCallback();
    }
    this.dispatchEvent(new Event('ents-layer-back', {composed: true, bubbles: true}));
  };

  private handleLayerClosed = () => {
    if (this.sizeLayerState.open) {
      this.sizeLayerState.update({open: false});
    }
    if (this.showLayer) {
      // this is needed because when base layer closes itself (e.g. click on 'x' or backdrop)
      // it is not automatically reflected on the 'showLayer' state
      this.showLayer = false;
    }
  };

  private renderItem(article: Article) {
    return html`
      <ents-size-layer-item
        .article="${article}"
        syan="${this.sizeLayerState.product!.syan}"
        color-id="${this.sizeLayerState.product!.colorId}"
        ?active="${this.sizeLayerState.selectedArticle?.id === article.id}"
        ?addToCartAfterSizeSelection="${this.sizeLayerState.triggerA2C}"
        data-test-ents-size-layer-item="${article.sizeOrQuantity}"
        ?showPrices="${!this.sizeLayerState.product!.allPricesEqual}">
      </ents-size-layer-item>
    `;
  }

  protected render() {
    return html`
      <ents-base-layer
        drawer-test-id="ents-size-layer"
        ?showLayer="${this.showLayer}"
        @ents-layer-closed="${this.handleLayerClosed}"
      >
        ${this.sizeLayerState.backButtonCallback != undefined
          ? getBackButton(this.messages, 'ents-size-layer', this.backButtonAction.bind(this))
          : nothing}
        ${this.sizeLayerState.product && this.sizeLayerState.open ? html`
          <h2 class="ents-heading" slot="title">
            ${this.messages['ents.wishlist.product.sizeLayerHeading']}
          </h2>
          <div class="ents-passformhinweis-container">
            <brtn-wishlist-size-layer syan="${this.sizeLayerState.product.syan}"
                                      farb-id="${this.sizeLayerState.product.colorId}"></brtn-wishlist-size-layer>
          </div>
          <div class="ents-size-layer__list" data-test-ents-size-layer-items>
            ${this.sizeLayerState.product.articles.map(article => this.renderItem(article))}
          </div>` : nothing}
      </ents-base-layer>`;
  }
}
